import { useLanguage } from "@/hooks/useLanguage";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import modernHotelLobby from "@/assets/modern-hotel-lobby.jpg";
import luxurySuite from "@/assets/luxury-suite.jpg";
import abstractPattern from "@/assets/abstract-pattern.jpg";
import SkylineHero from "@/components/SkylineHero";
const About = () => {
  const {
    t
  } = useLanguage();
  const heroAnimation = useScrollAnimation();
  const overviewAnimation = useScrollAnimation();
  const visionAnimation = useScrollAnimation();
  const valuesAnimation = useScrollAnimation();
  const metricsAnimation = useScrollAnimation();
  const differenceAnimation = useScrollAnimation();
  const globalAnimation = useScrollAnimation();
  const founderAnimation = useScrollAnimation();
  return <div className="min-h-screen bg-white text-foreground">
      <Header />
      
      <main className="pt-16 sm:pt-20">
        {/* Back button */}
        <div className="absolute top-4 left-4 sm:top-6 sm:left-6 md:top-8 md:left-8 z-20">
          <a 
            href="/"
            className="flex items-center text-muted-foreground hover:text-foreground transition-all duration-300 hover-scale group bg-background/80 backdrop-blur-sm rounded-full px-3 py-2 border border-border/50"
          >
            <svg className="mr-1 sm:mr-2 transition-transform duration-300 group-hover:-translate-x-1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m12 19-7-7 7-7"/>
              <path d="M19 12H5"/>
            </svg>
            <span className="text-xs sm:text-sm">Back to Home</span>
          </a>
        </div>
        
        {/* Hero Section */}
        <section className="min-h-[60vh] sm:min-h-[70vh] md:min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background relative overflow-hidden">
          {/* Skyline drawing animation — matches hotels hero */}
          <SkylineHero />
          
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:64px_64px] opacity-20"></div>
          <div className="absolute -top-32 sm:-top-40 -right-32 sm:-right-40 w-64 h-64 sm:w-80 sm:h-80 bg-primary/5 rounded-full blur-3xl animate-float"></div>
          <div className="absolute -bottom-32 sm:-bottom-40 -left-32 sm:-left-40 w-64 h-64 sm:w-80 sm:h-80 bg-secondary/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
          
          <div ref={heroAnimation.ref as any} className={`container mx-auto px-4 sm:px-6 relative z-10 ${heroAnimation.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="max-w-5xl mx-auto text-center space-y-4 sm:space-y-6 md:space-y-8 lg:space-y-12">
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-light leading-[0.9] tracking-tight text-foreground">Brassbell
Hospitality Group
              </h1>
                <div className="w-12 sm:w-16 md:w-20 lg:w-24 h-px bg-gradient-to-r from-primary to-secondary mx-auto"></div>
              </div>
              <h2 className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl 3xl:text-4xl font-light leading-tight text-muted-foreground max-w-4xl mx-auto">
                Shaping Tomorrow's Hospitality, Today
              </h2>
            </div>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-12 sm:py-16 md:py-20 lg:py-32 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-5 gap-6 sm:gap-8 md:gap-12 lg:gap-16 items-start">
                <div ref={overviewAnimation.ref as any} className={`lg:col-span-3 space-y-6 sm:space-y-8 md:space-y-12 ${overviewAnimation.isVisible ? 'animate-slide-in-left' : 'opacity-0'}`}>
                  <div className="space-y-4 sm:space-y-6 md:space-y-8">
                    <h3 className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground font-medium">
                      Our Story
                    </h3>
                    <div className="space-y-4 sm:space-y-6 md:space-y-8 text-sm sm:text-base md:text-lg leading-relaxed text-foreground">
                      <p className="hover-lift transition-all duration-300 p-1">
                        Brassbell Hospitality Group (BHG) is a next-generation hospitality and real estate platform that develops, operates, and invests in transformative destinations. We are more than an operator or a developer — we are a House of Brands, curating and scaling proprietary concepts that redefine the way people experience travel, living, and community.
                      </p>
                      <p className="hover-lift transition-all duration-300 p-1">
                        At the heart of BHG lies a portfolio of homegrown hospitality IPs — from boutique hotels and branded residences to lifestyle resorts and serviced apartments. Each brand is crafted with its own DNA, yet connected by a common philosophy: delivering timeless design, immersive experiences, and operational excellence at a global standard.
                      </p>
                      <p className="hover-lift transition-all duration-300 p-1">
                        Our dual expertise as both developers and operators enables us to control the entire value chain — from concept creation and asset development to long-term operations and brand stewardship. This integrated approach allows us to unlock value for investors, create enduring partnerships with developers and landowners, and deliver elevated experiences for our guests and residents.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-2 order-first lg:order-last">
                  <div className="relative">
                    <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl blur-xl animate-pulse"></div>
                    <div className="relative h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] bg-muted rounded-2xl overflow-hidden shadow-2xl border border-border hover-lift">
                      <img src="/lovable-uploads/a89f6123-3524-442a-970e-38d6e21ea613.png" alt="Modern Interior with Balcony View - Brassbell Hospitality" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Vision & Mission */}
        <section className="py-12 sm:py-16 md:py-20 lg:py-32 bg-gradient-to-br from-background via-muted/10 to-background">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
              <div ref={visionAnimation.ref as any} className={`grid lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16 lg:gap-20 ${visionAnimation.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
                <div className="space-y-6 sm:space-y-8 hover-lift transition-all duration-300 p-4 sm:p-6 rounded-2xl hover:bg-muted/20">
                  <div className="space-y-3 sm:space-y-4">
                    <h3 className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground font-medium">
                      Our Vision
                    </h3>
                    <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-primary to-secondary"></div>
                  </div>
                  <p className="text-base sm:text-lg leading-relaxed text-foreground">
                    At Brassbell Hospitality Group, our vision is not only to manage hotels and residences, but to redefine the way hospitality is imagined in our region. We aspire to be recognized as the leading incubator of hospitality brands that are born locally, yet resonate globally. By blending culture, creativity, and technology, we aim to set new benchmarks for lifestyle, luxury, and experiential travel. Our ultimate ambition is to build a portfolio of brands that reflect the spirit of our region while competing on the world stage—inspiring pride locally and admiration globally.
                  </p>
                </div>
                
                <div className="space-y-6 sm:space-y-8 hover-lift transition-all duration-300 p-4 sm:p-6 rounded-2xl hover:bg-muted/20">
                  <div className="space-y-3 sm:space-y-4">
                    <h3 className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground font-medium">
                      Our Mission
                    </h3>
                    <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-primary to-secondary"></div>
                  </div>
                  <p className="text-base sm:text-lg leading-relaxed text-foreground">
                    Our mission is rooted in a bold commitment to innovation and excellence. We design and operate hospitality concepts that go beyond accommodation—immersive environments where design, service, and digital technology converge to create seamless guest experiences. For our partners and investors, we deliver measurable value through smart operations, strong financial performance, and distinctive brand positioning. For our guests, we create personalized journeys that leave a lasting impact, whether through curated stays, inspired design, or meaningful cultural connections.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-12 sm:py-16 md:py-20 lg:py-32 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
              <div ref={valuesAnimation.ref as any} className={`text-center mb-12 sm:mb-16 md:mb-20 ${valuesAnimation.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
                <div className="space-y-3 sm:space-y-4">
                  <h2 className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground font-medium">
                    Core Values
                  </h2>
                  <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-primary to-secondary mx-auto"></div>
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 md:gap-16">
                {[{
                title: "Innovation with Intent",
                description: "We design not just for today, but for the future of hospitality."
              }, {
                title: "Ownership Mindset",
                description: "We act as stewards of every project, from conception to operation."
              }, {
                title: "Authenticity of Experience",
                description: "Each brand reflects a true sense of place, culture, and community."
              }, {
                title: "Excellence in Execution",
                description: "We deliver at the highest global standards, consistently."
              }, {
                title: "Partnership & Trust",
                description: "We believe in long-term, transparent collaborations."
              }].map((value, index) => <div key={index} className="space-y-4 sm:space-y-6 hover-lift transition-all duration-300 p-4 sm:p-6 rounded-2xl hover:bg-background hover:shadow-lg border border-transparent hover:border-border" style={{
                animationDelay: `${index * 0.1}s`
              }}>
                    <div className="space-y-3 sm:space-y-4">
                      <div className="w-6 sm:w-8 h-px bg-gradient-to-r from-primary to-secondary"></div>
                      <h3 className="text-lg sm:text-xl font-light text-foreground">{value.title}</h3>
                    </div>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {value.description}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* Visual Break with Abstract Pattern */}
        <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 relative overflow-hidden">
          <div className="absolute inset-0">
          </div>
          <div className="absolute -top-12 sm:-top-20 -right-12 sm:-right-20 w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 bg-primary/10 rounded-full blur-2xl animate-float"></div>
          <div className="absolute -bottom-12 sm:-bottom-20 -left-12 sm:-left-20 w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 bg-secondary/10 rounded-full blur-2xl animate-float" style={{
          animationDelay: '1s'
        }}></div>
          <div ref={metricsAnimation.ref as any} className={`container mx-auto px-4 sm:px-6 relative z-10 ${metricsAnimation.isVisible ? 'animate-scale-in' : 'opacity-0'}`}>
            <div className="max-w-4xl mx-auto text-center">
              <div className="space-y-6 sm:space-y-8">
              </div>
            </div>
          </div>
        </section>

        {/* The BHG Difference */}
        <section className="py-12 sm:py-16 md:py-20 lg:py-32 bg-background relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
              <div ref={differenceAnimation.ref as any} className={`grid lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16 lg:gap-20 items-center ${differenceAnimation.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
                <div className="space-y-8 sm:space-y-12">
                  <div className="space-y-4 sm:space-y-6">
                    <h2 className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground font-medium">
                      The BHG Difference
                    </h2>
                    <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-primary to-secondary"></div>
                  </div>
                  <p className="text-lg sm:text-xl md:text-2xl font-light leading-relaxed text-foreground hover-lift transition-all duration-300 p-1">
                    What sets BHG apart is our ability to operate as a brand creator, developer, and operator simultaneously. We do not simply manage properties; we imagine, design, and bring to life entire ecosystems of living and hospitality.
                  </p>
                  
                  {/* Feature highlights */}
                  <div className="space-y-4 sm:space-y-6">
                    {["House of Brands approach with proprietary concepts", "End-to-end control of the value chain", "Blend of development and operational expertise", "Focus on transformative destination creation"].map((feature, index) => <div key={index} className="flex items-center gap-3 sm:gap-4 hover-lift transition-all duration-300 p-2 rounded-lg hover:bg-muted/20" style={{
                    animationDelay: `${index * 0.1}s`
                  }}>
                        <div className="w-4 sm:w-6 h-px bg-gradient-to-r from-primary to-secondary"></div>
                        <span className="text-muted-foreground text-xs sm:text-sm">{feature}</span>
                      </div>)}
                  </div>
                </div>
                
                <div className="relative order-first lg:order-last">
                  <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl blur-xl animate-pulse"></div>
                  <div className="relative h-[300px] sm:h-[400px] md:h-[500px] bg-muted rounded-2xl overflow-hidden shadow-2xl border border-border hover-lift">
                    <img src="/lovable-uploads/f3ff9d04-89fa-4716-a4c5-9a2b329bc687.png" alt="Modern Interior Design with Archways" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Global Outlook & Commitment */}
        <section className="py-12 sm:py-16 md:py-20 lg:py-32 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
              <div ref={globalAnimation.ref as any} className={`grid lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16 lg:gap-20 ${globalAnimation.isVisible ? 'animate-slide-in-left' : 'opacity-0'}`}>
                <div className="space-y-6 sm:space-y-8 hover-lift transition-all duration-300 p-4 sm:p-6 rounded-2xl hover:bg-background hover:shadow-lg border border-transparent hover:border-border">
                  <div className="space-y-3 sm:space-y-4">
                    <h3 className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground font-medium">
                      Our Global Outlook
                    </h3>
                    <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-primary to-secondary"></div>
                  </div>
                  <p className="text-base sm:text-lg leading-relaxed text-foreground">
                    While rooted in emerging markets, BHG's outlook is global. We position ourselves at the intersection of hospitality investment and brand development, pursuing opportunities across cities, leisure destinations, and heritage assets worldwide. Our brands are designed to adapt to diverse markets, scaling with consistency while remaining true to local identity.
                  </p>
                </div>
                
                <div className="space-y-6 sm:space-y-8 hover-lift transition-all duration-300 p-4 sm:p-6 rounded-2xl hover:bg-background hover:shadow-lg border border-transparent hover:border-border">
                  <div className="space-y-3 sm:space-y-4">
                    <h3 className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground font-medium">
                      Our Commitment to the Future
                    </h3>
                    <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-primary to-secondary"></div>
                  </div>
                  <p className="text-base sm:text-lg leading-relaxed text-foreground">
                    At Brassbell Hospitality Group, success is measured not only by growth, but by the value we create — for our partners, our guests, and the communities we touch. Each destination we bring to life is a step toward reshaping the future of hospitality, guided by creativity, discipline, and a vision that endures.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Founder's Words */}
        <section className="py-12 sm:py-16 md:py-20 lg:py-32 bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 relative overflow-hidden">
          <div className="absolute -top-32 sm:-top-40 -right-32 sm:-right-40 w-64 h-64 sm:w-80 sm:h-80 bg-primary/10 rounded-full blur-3xl animate-float"></div>
          <div className="absolute -bottom-32 sm:-bottom-40 -left-32 sm:-left-40 w-64 h-64 sm:w-80 sm:h-80 bg-secondary/10 rounded-full blur-3xl animate-float" style={{
          animationDelay: '2s'
        }}></div>
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-4xl mx-auto">
              <div ref={founderAnimation.ref as any} className={`space-y-8 sm:space-y-12 ${founderAnimation.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
                <div className="space-y-4 sm:space-y-6 text-center">
                  <h2 className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground font-medium">
                    Words from Our Founder
                  </h2>
                  <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-primary to-secondary mx-auto"></div>
                </div>
                <blockquote className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-light leading-relaxed text-foreground text-center hover-lift transition-all duration-300 p-6 sm:p-8 rounded-2xl hover:bg-white/50 backdrop-blur-sm">
                  "At BHG, we believe hospitality is more than a business — it is a legacy. Our vision is to build Egyptian-born brands that resonate globally, inspire trust, and create meaningful impact. Every project we take on is an opportunity to honor our heritage while shaping the future of how people experience travel and living."
                </blockquote>
                <div className="text-center">
                  <cite className="text-base sm:text-lg text-muted-foreground hover-scale transition-all duration-300 inline-block">
                    Ahmed Ibrahim<br />
                    <span className="text-xs sm:text-sm uppercase tracking-[0.1em] text-muted-foreground/70">Founder & Chairman</span>
                  </cite>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
      
      <Footer />
    </div>;
};
export default About;